import React from 'react';
import { Drawer, Button, List, Avatar, Badge, Space, Typography, Divider, Empty, notification } from 'antd';
import { BellOutlined, ClearOutlined, ReloadOutlined, CheckCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { formatRelativeTime } from '../../utils/helper/format';

const { Text } = Typography;

const NotificationDrawer = ({
    open,
    onClose,
    notifications = [],
    loading = false,
    onRefresh,
    onClear,
    onMarkAsRead,
}) => {
    const unreadCount = notifications.filter(n => !n.read).length;

    const clearAllNotifications = () => {
        onClear?.();
        notification.success({
            message: 'Notifications Cleared',
            description: 'All notifications have been cleared successfully.',
            placement: 'topRight',
        });
    };

    const refreshNotifications = () => {
        onRefresh?.();
    };

    const markAsRead = (id) => {
        onMarkAsRead?.(id);
    };

    const openAction = (item, event) => {
        event.stopPropagation();
        const target = item.action || item.link || item.data?.link;
        if (target) {
            window.location.assign(target);
        }
    };

    const getIconByType = (type) => {
        switch (type?.toLowerCase()) {
            case 'success':
                return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
            case 'warning':
                return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
            case 'error':
            case 'critical':
                return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
            default:
                return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
        }
    };

    return (
        <Drawer
            title={
                <Space>
                    <BellOutlined />
                    <span>Notifications</span>
                    {unreadCount > 0 && <Badge count={unreadCount} size="small" />}
                </Space>
            }
            placement="left"
            onClose={onClose}
            zIndex={2019}
            open={open}
            width={600}
            extra={
                <Space>
                    <Button
                        type="text"
                        icon={<ReloadOutlined />}
                        onClick={refreshNotifications}
                        title="Refresh"
                        loading={loading}
                    />
                    <Button
                        type="text"
                        icon={<ClearOutlined />}
                        onClick={clearAllNotifications}
                        title="Clear All"
                        disabled={notifications.length === 0}
                    />
                </Space>
            }
        >
            {notifications.length === 0 ? (
                <Empty description="No notifications" style={{ marginTop: 100 }} />
            ) : (
                <List
                    itemLayout="horizontal"
                    dataSource={notifications}
                    renderItem={(item) => {
                        const action = item.action || item.link || item.data?.link;
                        return (
                            <List.Item
                                style={{
                                    backgroundColor: item.read ? 'transparent' : '#f0f8ff',
                                    padding: '12px',
                                    margin: '8px 0',
                                    borderRadius: '6px',
                                    border: item.read ? '1px solid #f0f0f0' : '1px solid #d9d9d9',
                                    cursor: 'pointer'
                                }}
                                onClick={() => markAsRead(item.id)}
                                actions={action ? [
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<LinkOutlined />}
                                        onClick={(event) => openAction(item, event)}
                                    >
                                        Open
                                    </Button>
                                ] : undefined}
                            >
                                <List.Item.Meta
                                    avatar={
                                        <Badge dot={!item.read}>
                                            <Avatar icon={<BellOutlined />} />
                                        </Badge>
                                    }
                                    title={
                                        <Space>
                                            {getIconByType(item.priority || item.type || item.notificationType)}
                                            <Text strong={!item.read}>{item.title || 'Notification'}</Text>
                                        </Space>
                                    }
                                    description={
                                        <div>
                                            <Text type="secondary">{item.message}</Text>
                                            <br />
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                {formatRelativeTime(item.timestamp)}
                                            </Text>
                                        </div>
                                    }
                                />
                            </List.Item>
                        );
                    }}
                />
            )}

            <Divider />

            <div style={{ textAlign: 'center', padding: '16px' }}>
                <Space direction="vertical">
                    <Button type="link" size="small">
                        View All Notifications
                    </Button>
                    <Button type="link" size="small">
                        Notification Settings
                    </Button>
                </Space>
            </div>
        </Drawer>
    );
};

export default NotificationDrawer;
